import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Row, Col, Card, Button, Badge, Form, 
  Alert, Modal, Tabs, Tab, ListGroup, Spinner,
  OverlayTrigger, Tooltip, Table, ProgressBar, ButtonGroup
} from 'react-bootstrap';
import { 
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle, 
  FaClock, FaFileAlt, FaRobot,
  FaHistory, FaStickyNote, FaExpand, FaCompress, FaDownload,
  FaEye, FaPencilAlt, FaUserShield, FaFileContract, FaSearch, FaTimes,
  FaSyncAlt, FaStamp, FaCalendarAlt, FaCrosshairs, FaUser, FaHashtag,
  FaUpload, FaInfoCircle, FaExternalLinkAlt
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PDFCanvasViewer from './PDFCanvasViewer';

interface DocumentForReview {
  id: string;
  document_id: string;
  transaction_id: string;
  filename: string;
  original_filename: string;
  document_type: string;
  description: string;
  s3_url: string;
  uploaded_at: string;
  added_at: string;
  review_status: string | null;
  ai_compliance_analysis: any;
  has_stamped_version?: boolean;
  original_s3_url?: string;

  
  transaction: {
    id: string;
    file_number: string;
    type: string;
    stage: string;
    client_name: string;
    property_address: string;
    agent_name: string;
    agent_email: string;
  };
  review_history: Array<{
    id: string;
    review_status: string;
    review_notes: string;
    reviewed_at: string;
    reviewed_by: string;
    compliance_score: number;
  }>;
}

interface DocumentReviewInterfaceProps {
  brokerId?: string;
  onDocumentReviewed?: (documentId: string, status: string) => void;
}

interface DocumentImage {
  page: number;
  dataUrl: string;
}

const DocumentReviewInterface: React.FC<DocumentReviewInterfaceProps> = ({ 
  brokerId, 
  onDocumentReviewed 
}) => {
  const [documents, setDocuments] = useState<DocumentForReview[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentForReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfPages, setPdfPages] = useState<any[]>([]);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [documentImages, setDocumentImages] = useState<DocumentImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [currentImagePage, setCurrentImagePage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewStatus, setReviewStatus] = useState<string>('');
  const [complianceScore, setComplianceScore] = useState(85);
  const [submitting, setSubmitting] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [newDocumentsAvailable, setNewDocumentsAvailable] = useState(false);
  const [lastDocumentCount, setLastDocumentCount] = useState(0);
  const [selectedDocumentAI, setSelectedDocumentAI] = useState<any>(null);
  const [loadingAI, setLoadingAI] = useState(false);
  
  // Stamp-related state
  const [applyStamp, setApplyStamp] = useState(true);
  const [stampDate, setStampDate] = useState<Date>(new Date());
  const [stampPositions, setStampPositions] = useState<{
    initials: { x: number; y: number; page: number } | null;
    date: { x: number; y: number; page: number } | null;
    fileNumber: { x: number; y: number; page: number } | null;
  }>({ initials: null, date: null, fileNumber: null });
  const [currentStampElement, setCurrentStampElement] = useState<'initials' | 'date' | 'fileNumber' | null>(null);
  const [fileNumber, setFileNumber] = useState<string>('');
  
  // Signature states
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [signaturePosition, setSignaturePosition] = useState<{ x: number; y: number; page: number } | null>(null);
  const [isDraggingSignature, setIsDraggingSignature] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [filter]);

  // Check for new documents every 2 minutes without refreshing the UI
  useEffect(() => {
    const checkForNewDocuments = async () => {
      try {
        const response = await axiosInstance.get('/broker-review/document-queue', {
          params: { status: filter === 'all' ? undefined : filter }
        });
        
        if (response.data.success) {
          const newCount = response.data.documents.length;
          // Show notification if there are more documents than before
          if (newCount > lastDocumentCount && lastDocumentCount > 0) {
            setNewDocumentsAvailable(true);
          }
        }
      } catch (err) {
        // Silently fail - this is just a background check
      }
    };

    // Check every 2 minutes
    const interval = setInterval(checkForNewDocuments, 120000);
    return () => clearInterval(interval);
  }, [filter, lastDocumentCount]);

  // Debug: Monitor state changes
  useEffect(() => {
    console.log('DocumentReviewInterface state updated:', {
      reviewStatus,
      reviewNotes,
      selectedDocument: selectedDocument?.document_id,
      showReviewModal,
      submitting
    });
  }, [reviewStatus, reviewNotes, selectedDocument, showReviewModal, submitting]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get('/broker-review/document-queue', {
        params: { status: filter === 'all' ? undefined : filter }
      });
      
      if (response.data.success) {
        // Sort documents by submission date (newest first)
        const sortedDocuments = response.data.documents.sort((a: DocumentForReview, b: DocumentForReview) => {
          // Use uploaded_at or added_at for sorting (whichever is available)
          const dateA = new Date(a.uploaded_at || a.added_at).getTime();
          const dateB = new Date(b.uploaded_at || b.added_at).getTime();
          return dateB - dateA; // Descending order (newest first)
        });
        
        // Debug: Log filter and documents received
        console.log(`📋 Filter: ${filter}, Documents received: ${sortedDocuments.length}`);
        console.log('Document statuses:', sortedDocuments.map((d: DocumentForReview) => ({ 
          id: d.id, 
          filename: d.filename, 
          review_status: d.review_status 
        })));
        
        setDocuments(sortedDocuments);
        setLastDocumentCount(sortedDocuments.length);
        setNewDocumentsAvailable(false); // Clear notification when documents are loaded
      } else {
        setError('Failed to load documents');
      }
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError('Unable to load documents for review');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewDocument = async (doc: DocumentForReview) => {
    setSelectedDocument(doc);
    setReviewNotes('');
    setReviewStatus('');
    setShowReviewModal(true);
    setSelectedDocumentAI(null);
    setLoadingAI(true);
    setDocumentImages([]);
    setLoadingImages(true);
    
    // If it's a PDF, try to convert to images for stamp placement
    if (doc.filename.toLowerCase().endsWith('.pdf')) {
      try {
        console.log('🖼️ Converting PDF to images for stamp placement...');
        const imagesResponse = await axiosInstance.get(`/broker-review/document-images/${doc.id}`);
        
        if (imagesResponse.data.success) {
          if (imagesResponse.data.images && imagesResponse.data.images.length > 0) {
            setDocumentImages(imagesResponse.data.images);
            console.log('✅ Converted PDF to', imagesResponse.data.images.length, 'images');
          } else if (imagesResponse.data.directUrl) {
            // Update document URL with pre-signed URL
            doc.s3_url = imagesResponse.data.directUrl;
            console.log('✅ Using pre-signed URL for PDF display');
          }
        } else {
          console.warn('⚠️ Could not convert PDF to images');
        }
      } catch (error) {
        console.error('❌ Error converting PDF to images:', error);
      } finally {
        setLoadingImages(false);
      }
    } else {
      setLoadingImages(false);
    }
    
    // Fetch AI analysis data separately
    try {
      const response = await axiosInstance.get('/broker-review/ai-analyzed-documents');
      if (response.data.success) {
        // Find the AI data for this specific document
        const aiDoc = response.data.documents.find((d: any) => d.document_id === doc.document_id || d.document_id === doc.id);
        if (aiDoc && aiDoc.ai_compliance_analysis) {
          setSelectedDocumentAI(aiDoc.ai_compliance_analysis);
          setComplianceScore(aiDoc.ai_compliance_analysis.compliance_score || 85);
        } else {
          setComplianceScore(85);
        }
      }
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      setComplianceScore(85);
    } finally {
      setLoadingAI(false);
    }
  };

  // Handle document click for stamp placement
  const handleDocumentClick = (event: React.MouseEvent<HTMLDivElement | HTMLCanvasElement | HTMLImageElement>, pageNumber: number = 1) => {
    if (!currentStampElement || !applyStamp) return;
    
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    setStampPositions(prev => ({
      ...prev,
      [currentStampElement]: { x, y, page: pageNumber }
    }));
    
    setCurrentStampElement(null); // Clear selection after placement
    
    toast.success(`${currentStampElement.charAt(0).toUpperCase() + currentStampElement.slice(1)} placed on page ${pageNumber}!`);
  };

  // Handle document drag and drop for stamp placement
  const handleDocumentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.style.background = 'transparent';
    
    if (!applyStamp) return;
    
    const elementType = event.dataTransfer.getData('elementType');
    if (!elementType) return;
    
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    if (elementType === 'signature') {
      setSignaturePosition({ x, y, page: 1 });
      toast.success('Signature placed successfully!');
    } else {
      setStampPositions(prev => ({
        ...prev,
        [elementType as 'initials' | 'date' | 'fileNumber']: { x, y, page: 1 }
      }));
      toast.success(`${elementType.charAt(0).toUpperCase() + elementType.slice(1)} placed successfully!`);
    }
    
    setCurrentStampElement(null); // Clear selection after placement
  };

  const submitReview = async () => {
    console.log('🚀 submitReview function started');
    console.log('Submit clicked:', { reviewStatus, reviewNotes, selectedDocument: selectedDocument?.document_id });
    console.log('Full state:', { 
      selectedDocument, 
      reviewStatus, 
      reviewNotes, 
      submitting,
      disabledConditions: {
        noReviewStatus: !reviewStatus,
        isSubmitting: submitting,
        needsNotesButEmpty: ((reviewStatus === 'needs_revision' || reviewStatus === 'rejected') && !reviewNotes)
      }
    });
    
    if (!selectedDocument) {
      console.error('❌ No selectedDocument!');
      toast.error('No document selected');
      return;
    }
    
    if (!reviewStatus) {
      console.error('❌ No reviewStatus!');
      toast.error('Please select a review status');
      return;
    }
    
    if ((reviewStatus === 'needs_revision' || reviewStatus === 'rejected') && !reviewNotes) {
      console.error('❌ Review notes required but empty!');
      toast.error('Please provide review notes when requesting revision or rejecting');
      return;
    }

    console.log('✅ All validations passed, starting API call...');

    try {
      setSubmitting(true);
      console.log('📝 Submitting state set to true');
      
      // Log what we're sending
      const payload = {
        document_id: selectedDocument.document_id || selectedDocument.id,
        review_status: reviewStatus,
        review_notes: reviewNotes || '',
        compliance_score: complianceScore,
        compliance_concerns: {} // Will be enhanced later
      };
      
      console.log('📤 Sending review payload:', payload);
      console.log('📤 About to call axiosInstance.post...');
      
      const response = await axiosInstance.post('/broker-review/review-document', payload);

      console.log('📥 Got response:', response);

      if (response.data.success) {
        console.log('✅ Review submitted successfully!');
        
        // If approved and stamping is enabled, apply the stamp
        if (reviewStatus === 'approved' && applyStamp) {
          try {
            console.log('🏷️ Applying broker stamp...');
            
            // Automatic stamp placement - bottom right corner, arranged horizontally
            const autoStampPositions = {
              file_number: { x: 50, y: 92, page: 999 },  // File No - left
              initials: { x: 70, y: 92, page: 999 },     // Initials - center  
              date: { x: 85, y: 92, page: 999 }          // Date - right
            };
            
            const stampResponse = await axiosInstance.post('/broker-review/stamp-document', {
              documentId: selectedDocument.document_id || selectedDocument.id,
              customDate: stampDate,
              stampOptions: {
                positions: stampPositions.initials ? stampPositions : autoStampPositions,
                signature: signaturePosition && signatureDataUrl ? {
                  position: signaturePosition || { x: 50, y: 80, page: 999 },
                  dataUrl: signatureDataUrl
                } : null
              },
              fileNumber: fileNumber || undefined
            });

            if (stampResponse.data.success) {
              console.log('✅ Document stamped successfully:', stampResponse.data.stampedDocument);
              
              // Update the documents list to reflect the stamped version
              if (stampResponse.data.stampedDocument?.url) {
                // Update the document in the list
                setDocuments(prevDocs => 
                  prevDocs.map(doc => 
                    doc.id === selectedDocument.id 
                      ? { ...doc, has_stamped_version: true, s3_url: stampResponse.data.stampedDocument.url }
                      : doc
                  )
                );
                
                // CRITICAL: Update the currently displayed document to show stamped version
                setSelectedDocument(prev => ({
                  ...prev!,
                  s3_url: stampResponse.data.stampedDocument.url,
                  has_stamped_version: true
                }));
                
                // Clear document images to force PDF display of stamped version
                setDocumentImages([]);
                setLoadingImages(false);
                
                // Log the stamped document URL for debugging
                console.log('📋 Stamped document URL:', stampResponse.data.stampedDocument.url);
                console.log('📋 Updated selectedDocument:', {
                  id: selectedDocument.id,
                  s3_url: stampResponse.data.stampedDocument.url,
                  has_stamped_version: true
                });
                
                // The stamped URL is already set above, no need to fetch again
              }
              
              toast.success('Document approved and stamped! The stamped version is now displayed.');
              
              // Don't open in new tab - show in current view
              // Reset stamp positions for clean UI
              setStampPositions({ initials: null, date: null, fileNumber: null });
              setApplyStamp(false); // Hide stamp UI since it's already applied
            } else {
              console.warn('⚠️ Document approved but stamping failed:', stampResponse.data.error);
              toast.warning('Document approved but stamp could not be applied');
            }
          } catch (stampErr) {
            console.warn('⚠️ Document approved but stamping failed:', stampErr);
            toast.warning('Document approved but stamp could not be applied');
          }
        } else {
          toast.success(`Document ${reviewStatus === 'approved' ? 'approved' : 'marked for revision'}`);
        }
        
        // Don't close modal if we just stamped - let user see the result
        if (!(reviewStatus === 'approved' && applyStamp)) {
          setShowReviewModal(false);
          fetchDocuments(); // Refresh list only if closing modal
        } else {
          // If we stamped, update documents list without resetting selected document
          setDocuments(prevDocs => 
            prevDocs.map(doc => 
              doc.id === selectedDocument.id 
                ? { ...doc, review_status: 'approved', has_stamped_version: true }
                : doc
            )
          );
        }
        
        if (onDocumentReviewed) {
          onDocumentReviewed(selectedDocument.document_id || selectedDocument.id, reviewStatus);
        }
      } else {
        console.error('❌ Response indicated failure:', response.data);
        toast.error('Review submission failed');
      }
    } catch (err: any) {
      console.error('❌ CATCH BLOCK: Error submitting review:', err);
      console.error('Error type:', err.constructor.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      console.error('Error response:', err.response);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        config: err.config
      });
      toast.error(err.response?.data?.error || err.message || 'Failed to submit review');
    } finally {
      console.log('🏁 Finally block: setting submitting to false');
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'pending_review':
        return <Badge bg="warning" text="dark"><FaClock className="me-1" />Pending</Badge>;
      case 'approved':
        return <Badge bg="success"><FaCheckCircle className="me-1" />Approved</Badge>;
      case 'needs_revision':
        return <Badge bg="danger"><FaExclamationTriangle className="me-1" />Needs Revision</Badge>;
      case 'rejected':
        return <Badge bg="dark"><FaTimesCircle className="me-1" />Rejected</Badge>;
      default:
        return <Badge bg="secondary">Not Reviewed</Badge>;
    }
  };

  const getComplianceScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'danger';
  };

  const calculateDaysInQueue = (dateString: string) => {
    const submittedDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - submittedDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDaysInQueueBadge = (days: number) => {
    if (days <= 2) return 'success';
    if (days <= 5) return 'warning';
    return 'danger';
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchTerm === '') return true;
    
    const searchLower = searchTerm.toLowerCase().trim();
    const searchTerms = searchLower.split(' ').filter(term => term.length > 0);
    
    // Create searchable text from all relevant fields
    const searchableText = [
      doc.filename,
      doc.original_filename,
      doc.document_type,
      doc.description,
      doc.transaction.file_number,
      doc.transaction.client_name,
      doc.transaction.property_address,
      doc.transaction.agent_name,
      doc.transaction.agent_email,
      doc.transaction.type,
      doc.review_status?.replace(/_/g, ' '),
      // Add document ID (partial match)
      doc.id.substring(0, 8),
      doc.document_id?.substring(0, 8)
    ].filter(Boolean).join(' ').toLowerCase();
    
    // Check if ALL search terms are found (AND search)
    const allTermsMatch = searchTerms.every(term => searchableText.includes(term));
    
    // Also check if the entire search phrase is found (for exact matches)
    const exactMatch = searchableText.includes(searchLower);
    
    return allTermsMatch || exactMatch;
  });

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              <FaFileAlt className="me-2" />
              Document Review Queue
            </h2>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fetchDocuments()}
              disabled={loading}
            >
              <FaSyncAlt className={`me-1 ${loading ? 'fa-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {/* New Documents Notification */}
          {newDocumentsAvailable && (
            <Alert variant="info" className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <FaExclamationTriangle className="me-2" />
                New documents have been added to the queue
              </div>
              <Button 
                variant="info" 
                size="sm"
                onClick={() => fetchDocuments()}
              >
                <FaFileAlt className="me-1" />
                Refresh Queue
              </Button>
            </Alert>
          )}
          
          {/* Filters and Search */}
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">
                  {searchTerm ? (
                    <>Found {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} matching "{searchTerm}"</>
                  ) : (
                    <>Showing {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}</>
                  )}
                </div>
                <div className="text-muted small">
                  <FaClock className="me-1" />
                  Sorted by: Newest First
                </div>
              </div>
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="position-relative">
                    <FaSearch 
                      className="position-absolute text-muted" 
                      style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} 
                    />
                    <Form.Control
                      type="text"
                      placeholder="Search documents, clients, properties, agents, transaction #, status..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="mb-2 mb-md-0 ps-4 pe-4"
                      title="Search by: document name, type, client name, property address, agent name/email, transaction number, status, or document ID"
                    />
                    {searchTerm && (
                      <Button
                        variant="link"
                        className="position-absolute p-0 text-muted"
                        style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                        onClick={() => setSearchTerm('')}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex gap-2 justify-content-md-end">
                    <Button
                      variant={filter === 'all' ? 'primary' : 'outline-primary'}
                      size="sm"
                      onClick={() => setFilter('all')}
                    >
                      All ({documents.length})
                    </Button>
                    <Button
                      variant={filter === 'pending' ? 'warning' : 'outline-warning'}
                      size="sm"
                      onClick={() => setFilter('pending')}
                    >
                      <FaClock className="me-1" />
                      Pending ({documents.filter(d => !d.review_status || d.review_status === 'pending_review').length})
                    </Button>
                    <Button
                      variant={filter === 'approved' ? 'success' : 'outline-success'}
                      size="sm"
                      onClick={() => setFilter('approved')}
                    >
                      <FaCheckCircle className="me-1" />
                      Approved ({documents.filter(d => d.review_status === 'approved').length})
                    </Button>
                    <Button
                      variant={filter === 'rejected' ? 'danger' : 'outline-danger'}
                      size="sm"
                      onClick={() => setFilter('rejected')}
                    >
                      <FaTimesCircle className="me-1" />
                      Needs Revision ({documents.filter(d => d.review_status === 'needs_revision' || d.review_status === 'rejected').length})
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Document Views */}
          <Card className="mb-3">
            <Card.Body>
              <Tabs defaultActiveKey="cards" className="mb-3">
                <Tab eventKey="cards" title="Card View">
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2">Loading documents...</p>
                    </div>
                  ) : error ? (
                    <Alert variant="danger">
                      <FaExclamationTriangle className="me-2" />
                      {error}
                    </Alert>
                  ) : filteredDocuments.length === 0 ? (
                    <Alert variant="info">
                      <FaFileAlt className="me-2" />
                      No documents found matching your criteria.
                    </Alert>
                  ) : (
                    <Row>
                      {filteredDocuments.map((doc) => (
                        <Col lg={6} xl={4} key={doc.id} className="mb-3">
                          <Card className="h-100 shadow-sm">
                            <Card.Header className="bg-light">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">{doc.filename}</h6>
                                  <small className="text-muted d-block">{doc.document_type}</small>
                                </div>
                                <div className="d-flex flex-column align-items-end">
                                  {getStatusBadge(doc.review_status)}
                                  <Badge 
                                    bg={getDaysInQueueBadge(calculateDaysInQueue(doc.added_at || doc.uploaded_at))} 
                                    className="mt-1"
                                    pill
                                  >
                                    <FaClock size={10} className="me-1" />
                                    {calculateDaysInQueue(doc.added_at || doc.uploaded_at)}d
                                  </Badge>
                                </div>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <ListGroup variant="flush" className="mb-3">
                                <ListGroup.Item className="px-0 py-2">
                                  <small className="text-muted">Transaction:</small>
                                  <div className="fw-semibold">{doc.transaction.file_number}</div>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-0 py-2">
                                  <small className="text-muted">Submitted:</small>
                                  <div>
                                    {new Date(doc.uploaded_at || doc.added_at).toLocaleDateString()} at{' '}
                                    {new Date(doc.uploaded_at || doc.added_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-0 py-2">
                                  <small className="text-muted">Client:</small>
                                  <div>{doc.transaction.client_name}</div>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-0 py-2">
                                  <small className="text-muted">Property:</small>
                                  <div>{doc.transaction.property_address}</div>
                                </ListGroup.Item>
                                <ListGroup.Item className="px-0 py-2">
                                  <small className="text-muted">Agent:</small>
                                  <div>{doc.transaction.agent_name}</div>
                                  <small>{doc.transaction.agent_email}</small>
                                </ListGroup.Item>
                              </ListGroup>
                              
                              {/* AI Analysis Summary */}
                              {doc.ai_compliance_analysis && (
                                <div className="mb-3">
                                  <small className="text-muted d-block mb-1">
                                    <FaRobot className="me-1" />
                                    AI Compliance Score
                                  </small>
                                  <div className="d-flex align-items-center">
                                    <div className="progress flex-grow-1 me-2" style={{ height: '10px' }}>
                                      <div 
                                        className={`progress-bar bg-${getComplianceScoreColor(doc.ai_compliance_analysis.compliance_score)}`}
                                        style={{ width: `${doc.ai_compliance_analysis.compliance_score}%` }}
                                      />
                                    </div>
                                    <Badge bg={getComplianceScoreColor(doc.ai_compliance_analysis.compliance_score)}>
                                      {doc.ai_compliance_analysis.compliance_score}%
                                    </Badge>
                                  </div>
                                  {doc.ai_compliance_analysis.issues?.length > 0 && (
                                    <small className="text-danger d-block mt-1">
                                      {doc.ai_compliance_analysis.issues.length} issues found
                                    </small>
                                  )}
                                </div>
                              )}
                              
                              <div className="d-grid gap-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleReviewDocument(doc)}
                                >
                                  <FaEye className="me-1" />
                                  Review Document
                                </Button>
                                {doc.review_history?.length > 0 && (
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>
                                        Last reviewed: {new Date(doc.review_history[0].reviewed_at).toLocaleDateString()}
                                      </Tooltip>
                                    }
                                  >
                                    <Button variant="outline-secondary" size="sm">
                                      <FaHistory className="me-1" />
                                      Review History ({doc.review_history.length})
                                    </Button>
                                  </OverlayTrigger>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Tab>
                
                <Tab eventKey="table" title="Agent/Transaction View">
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2">Loading documents...</p>
                    </div>
                  ) : error ? (
                    <Alert variant="danger">
                      <FaExclamationTriangle className="me-2" />
                      {error}
                    </Alert>
                  ) : filteredDocuments.length === 0 ? (
                    <Alert variant="info">
                      <FaFileAlt className="me-2" />
                      No documents found matching your criteria.
                    </Alert>
                  ) : (
                    <div className="table-responsive">
                      {/* Group documents by agent and transaction */}
                      {(() => {
                        // Group documents by agent and transaction
                        const groupedData: Record<string, Record<string, typeof filteredDocuments>> = {};
                        
                        filteredDocuments.forEach(doc => {
                          const agentKey = doc.transaction.agent_name;
                          const transactionKey = `${doc.transaction.file_number}|||${doc.transaction.client_name}|||${doc.transaction.property_address}`;
                          
                          if (!groupedData[agentKey]) {
                            groupedData[agentKey] = {};
                          }
                          if (!groupedData[agentKey][transactionKey]) {
                            groupedData[agentKey][transactionKey] = [];
                          }
                          groupedData[agentKey][transactionKey].push(doc);
                        });
                        
                        return (
                          <Table hover className="mb-0">
                            <thead>
                              <tr>
                                <th style={{ width: '250px' }}>Agent / Transaction</th>
                                <th>Document</th>
                                <th style={{ width: '120px' }}>Status</th>
                                <th style={{ width: '80px' }}>Age</th>
                                <th style={{ width: '100px' }}>Compliance</th>
                                <th style={{ width: '120px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(groupedData).map(([agent, transactions]) => (
                                <React.Fragment key={agent}>
                                  <tr className="table-primary">
                                    <td colSpan={6} className="fw-bold">
                                      <FaUserShield className="me-2" />
                                      {agent}
                                    </td>
                                  </tr>
                                  {Object.entries(transactions).map(([transactionInfo, docs]) => {
                                    const [fileNumber, clientName, propertyAddress] = transactionInfo.split('|||');
                                    return (
                                      <React.Fragment key={transactionInfo}>
                                        <tr className="table-light">
                                          <td colSpan={6} className="ps-4">
                                            <div className="d-flex justify-content-between">
                                              <div>
                                                <FaFileContract className="me-2 text-muted" />
                                                <span className="fw-semibold">{fileNumber}</span> - {clientName}
                                                <div className="small text-muted ps-4">{propertyAddress}</div>
                                              </div>
                                              <div className="text-muted small">
                                                {docs.length} document{docs.length > 1 ? 's' : ''}
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                        {docs.map(doc => (
                                          <tr key={doc.id}>
                                            <td></td>
                                            <td className="ps-5">
                                              {doc.filename}
                                              <div className="small text-muted">{doc.document_type}</div>
                                            </td>
                                            <td>{getStatusBadge(doc.review_status)}</td>
                                            <td>
                                              <Badge 
                                                bg={getDaysInQueueBadge(calculateDaysInQueue(doc.added_at || doc.uploaded_at))}
                                                pill
                                              >
                                                {calculateDaysInQueue(doc.added_at || doc.uploaded_at)}d
                                              </Badge>
                                            </td>
                                            <td>
                                              {doc.ai_compliance_analysis && (
                                                <div className="d-flex align-items-center">
                                                  <div className="progress flex-grow-1 me-2" style={{ height: '8px', width: '60px' }}>
                                                    <div 
                                                      className={`progress-bar bg-${getComplianceScoreColor(doc.ai_compliance_analysis.compliance_score)}`}
                                                      style={{ width: `${doc.ai_compliance_analysis.compliance_score}%` }}
                                                    />
                                                  </div>
                                                  <small>{doc.ai_compliance_analysis.compliance_score}%</small>
                                                </div>
                                              )}
                                            </td>
                                            <td>
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleReviewDocument(doc)}
                                              >
                                                <FaEye className="me-1" />
                                                Review
                                              </Button>
                                            </td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </Table>
                        );
                      })()}
                    </div>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Review Modal */}
      <Modal
        show={showReviewModal}
        onHide={() => !submitting && setShowReviewModal(false)}
        size="xl"
        fullscreen={fullScreenPreview ? true : undefined}
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>
            Review: {selectedDocument?.filename}
            <Button
              variant="link"
              className="ms-3"
              onClick={() => setFullScreenPreview(!fullScreenPreview)}
            >
              {fullScreenPreview ? <FaCompress /> : <FaExpand />}
            </Button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <Row>
              <Col lg={fullScreenPreview ? 8 : 7}>
                {/* Document Preview */}
                <Card className="mb-3">
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Document Preview</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            // Fetch fresh pre-signed URL for download
                            const urlResponse = await axiosInstance.get(`/broker-review/document-url/${selectedDocument.id}`);
                            if (urlResponse.data.success && urlResponse.data.url) {
                              window.open(urlResponse.data.url, '_blank');
                            } else {
                              toast.error('Could not generate download link');
                            }
                          } catch (error) {
                            console.error('Error getting download URL:', error);
                            toast.error('Failed to download document');
                          }
                        }}
                      >
                        <FaDownload className="me-1" />
                        Download
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body style={{ height: fullScreenPreview ? '80vh' : '600px', overflow: 'auto', position: 'relative' }}>
                    {(
                      <div 
                        style={{ 
                          position: 'relative', 
                          width: '100%', 
                          height: '100%',
                          cursor: currentStampElement ? 'crosshair' : 'default'
                        }}
                        onClick={(e) => handleDocumentClick(e, 1)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.background = 'rgba(0, 123, 255, 0.1)';
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                        onDrop={handleDocumentDrop}
                      >
                      {loadingImages ? (
                          <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading document...</p>
                          </div>
                        ) : documentImages.length > 0 ? (
                          <div>
                            {documentImages.length > 1 && (
                              <div className="d-flex justify-content-center mb-3">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  disabled={currentImagePage === 1}
                                  onClick={() => setCurrentImagePage(currentImagePage - 1)}
                                >
                                  Previous Page
                                </Button>
                                <span className="mx-3 align-self-center">
                                  Page {currentImagePage} of {documentImages.length}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  disabled={currentImagePage === documentImages.length}
                                  onClick={() => setCurrentImagePage(currentImagePage + 1)}
                                >
                                  Next Page
                                </Button>
                              </div>
                            )}
                            <img
                              src={documentImages[currentImagePage - 1].dataUrl}
                              alt={`Page ${currentImagePage}`}
                              className="img-fluid"
                              style={{ 
                                cursor: currentStampElement ? 'crosshair' : 'default',
                                maxWidth: '100%',
                                height: 'auto'
                              }}
                              onClick={(e) => handleDocumentClick(e, currentImagePage)}
                            />
                          </div>
                        ) : selectedDocument.s3_url ? (
                          // Show PDF - simple approach that was working
                          <div>
                            <embed
                              src={selectedDocument.s3_url}
                              type="application/pdf"
                              width="100%"
                              height="500px"
                              style={{ minHeight: '500px' }}
                            />
                            {selectedDocument.has_stamped_version && (
                              <Alert variant="success" className="mt-2">
                                <FaCheckCircle className="me-2" />
                                Viewing stamped document
                              </Alert>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <Alert variant="info">
                              <FaFileAlt className="me-2" />
                              Document preview is being prepared...
                              <div className="mt-3">
                                <a
                                  href={selectedDocument.s3_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-primary"
                                >
                                  <FaExternalLinkAlt className="me-1" />
                                  Open Document
                                </a>
                              </div>
                            </Alert>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={fullScreenPreview ? 4 : 5}>
                <Tabs defaultActiveKey="review" className="mb-3">
                  <Tab eventKey="review" title="Review">
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Label>Review Status</Form.Label>
                        <div className="d-grid gap-2">
                          <Button
                            variant={reviewStatus === 'approved' ? 'success' : 'outline-success'}
                            onClick={() => {
                              console.log('Approve button clicked');
                              setReviewStatus('approved');
                            }}
                            disabled={submitting}
                          >
                            <FaCheckCircle className="me-2" />
                            Approve Document
                          </Button>
                          <Button
                            variant={reviewStatus === 'needs_revision' ? 'warning' : 'outline-warning'}
                            onClick={() => {
                              console.log('Request Revision button clicked');
                              setReviewStatus('needs_revision');
                            }}
                            disabled={submitting}
                          >
                            <FaPencilAlt className="me-2" />
                            Request Revision
                          </Button>
                          <Button
                            variant={reviewStatus === 'rejected' ? 'danger' : 'outline-danger'}
                            onClick={() => {
                              console.log('Reject button clicked');
                              setReviewStatus('rejected');
                            }}
                            disabled={submitting}
                          >
                            <FaTimesCircle className="me-2" />
                            Reject Document
                          </Button>
                        </div>
                      </Form.Group>

                      {reviewStatus && (
                        <Alert variant="info" className="mb-3">
                          <small>
                            <strong>Selected action:</strong> {
                              reviewStatus === 'approved' ? '✅ Approve' : 
                              reviewStatus === 'needs_revision' ? '⚠️ Request Revision' : 
                              '❌ Reject'
                            }
                          </small>
                        </Alert>
                      )}
                      
                      {/* AI Compliance Analysis - Full Details */}
                      {loadingAI ? (
                        <Card className="mb-3">
                          <Card.Body className="text-center py-4">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <p className="mt-2 mb-0 text-muted">Loading AI analysis...</p>
                          </Card.Body>
                        </Card>
                      ) : selectedDocumentAI ? (
                        <>
                          {/* AI Analysis Visual Display */}
                          <Card className="mb-3">
                            <Card.Body>
                              <h6 className="mb-3">
                                <FaRobot className="me-2 text-primary" />
                                AI Analysis Results
                              </h6>
                              
                              {/* Score Cards */}
                              <Row className="mb-3">
                                <Col md={6}>
                                  <div className="text-center p-3 border rounded">
                                    <small className="text-muted d-block">Compliance Score</small>
                                    <div className="h3 mb-0">
                                      <Badge bg={
                                        selectedDocumentAI.compliance_score >= 90 ? 'success' :
                                        selectedDocumentAI.compliance_score >= 70 ? 'warning' : 'danger'
                                      }>
                                        {selectedDocumentAI.compliance_score}%
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="text-center p-3 border rounded">
                                    <small className="text-muted d-block">Risk Level</small>
                                    <div className="h5 mb-0 mt-2">
                                      <Badge bg={
                                        selectedDocumentAI.risk_level === 'low' ? 'success' :
                                        selectedDocumentAI.risk_level === 'medium' ? 'warning' : 'danger'
                                      }>
                                        {selectedDocumentAI.compliance_score === 0 ? 'CRITICAL' : 
                                         selectedDocumentAI.compliance_score >= 90 ? 'LOW' :
                                         selectedDocumentAI.compliance_score >= 70 ? 'MEDIUM' : 'HIGH'}
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              {/* Signature Analysis - Enhanced */}
                              {selectedDocumentAI.field_analysis?.signature_details && (
                                <div className="mb-3">
                                  <h6 className="small text-muted mb-2">Signature Analysis</h6>
                                  <Card className={`border-${selectedDocumentAI.validation_summary?.invalid_signatures?.length > 0 ? 'danger' : selectedDocumentAI.field_analysis?.signatures_missing?.length > 0 ? 'warning' : 'success'}`}>
                                    <Card.Body className="py-2">
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span>
                                          <strong>{selectedDocumentAI.field_analysis.signature_details.length}</strong> signatures detected
                                        </span>
                                        <div>
                                          {selectedDocumentAI.validation_summary?.invalid_signatures?.length > 0 && (
                                            <Badge bg="danger" className="me-1">
                                              {selectedDocumentAI.validation_summary.invalid_signatures.length} Invalid
                                            </Badge>
                                          )}
                                          {selectedDocumentAI.field_analysis?.signatures_missing?.length > 0 && (
                                            <Badge bg="warning">
                                              {selectedDocumentAI.field_analysis.signatures_missing.length} Missing
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Valid Signatures */}
                                      {selectedDocumentAI.field_analysis?.signatures_present?.length > 0 && (
                                        <div className="bg-light rounded p-2 mb-2">
                                          <small className="d-block mb-1"><strong>✅ Valid Signatures:</strong></small>
                                          <div className="text-success fw-semibold">{selectedDocumentAI.field_analysis.signatures_present.join(', ')}</div>
                                        </div>
                                      )}
                                      
                                      {/* Invalid Signatures - Better formatting */}
                                      {selectedDocumentAI.validation_summary?.invalid_signatures?.length > 0 && (
                                        <div className="bg-danger bg-opacity-10 border border-danger rounded p-2 mb-2">
                                          <small className="d-block mb-1 text-danger"><strong>❌ Invalid Signatures:</strong></small>
                                          <div className="text-dark fw-semibold">{selectedDocumentAI.validation_summary.invalid_signatures.join(', ')}</div>
                                          <small className="text-muted">These signers are not transaction parties</small>
                                        </div>
                                      )}
                                      
                                      {/* Missing Signatures */}
                                      {selectedDocumentAI.field_analysis?.signatures_missing?.length > 0 && (
                                        <div className="bg-warning bg-opacity-10 border border-warning rounded p-2">
                                          <small className="d-block mb-1 text-warning-emphasis"><strong>⚠️ Missing Signatures:</strong></small>
                                          <div className="text-dark fw-semibold">{selectedDocumentAI.field_analysis.signatures_missing.join(', ')}</div>
                                        </div>
                                      )}
                                    </Card.Body>
                                  </Card>
                                </div>
                              )}

                              {/* Compliance Issues - Enhanced Display with Better Formatting */}
                              {selectedDocumentAI.issues?.length > 0 && (
                                <div className="mb-3">
                                  <h6 className="small text-muted mb-2">
                                    <FaExclamationTriangle className="me-1" />
                                    Issues Found ({selectedDocumentAI.issues.length})
                                  </h6>
                                  {selectedDocumentAI.issues.map((issue: any, idx: number) => (
                                    <Card key={idx} className={`mb-2 border-${issue.severity === 'critical' ? 'danger' : 'warning'}`}>
                                      <Card.Body className="py-2">
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                              <Badge bg={issue.severity === 'critical' ? 'danger' : 'warning'} className="me-2">
                                                {issue.severity}
                                              </Badge>
                                              <strong className="text-dark">{issue.type?.replace(/_/g, ' ').toUpperCase()}</strong>
                                            </div>
                                            <p className="mb-1 text-dark">{issue.message}</p>
                                            {issue.details && (
                                              <div className="bg-light rounded p-2 mt-2">
                                                {issue.details.signer && (
                                                  <div className="small">
                                                    <strong>Signer Found:</strong> <span className="text-danger fw-semibold">{issue.details.signer}</span>
                                                  </div>
                                                )}
                                                {issue.details.transaction_parties && (
                                                  <div className="small mt-1">
                                                    <strong>Expected Parties:</strong> <span className="text-success">{issue.details.transaction_parties.join(', ')}</span>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </Card.Body>
                                    </Card>
                                  ))}
                                </div>
                              )}

                              {/* Validation Summary */}
                              {selectedDocumentAI.validation_summary && (
                                <div className="mb-3">
                                  <h6 className="small text-muted mb-2">Signature Validation</h6>
                                  <div className="border rounded p-2">
                                    <Row className="g-2">
                                      <Col md={6}>
                                        <small className="text-muted d-block">Required Signers:</small>
                                        <div className="text-success">
                                          {selectedDocumentAI.validation_summary.required_signers?.length > 0 
                                            ? selectedDocumentAI.validation_summary.required_signers.join(', ')
                                            : 'None'
                                          }
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <small className="text-muted d-block">Actual Signers:</small>
                                        <div className={selectedDocumentAI.validation_summary.invalid_signatures?.length > 0 ? 'text-danger' : 'text-success'}>
                                          {selectedDocumentAI.validation_summary.actual_signers?.length > 0 
                                            ? selectedDocumentAI.validation_summary.actual_signers.join(', ')
                                            : 'None found'
                                          }
                                        </div>
                                      </Col>
                                    </Row>
                                    {selectedDocumentAI.validation_summary.invalid_signatures?.length > 0 && (
                                      <div className="mt-2">
                                        <small className="text-danger d-block">Invalid Signatures:</small>
                                        <div className="text-danger">
                                          {selectedDocumentAI.validation_summary.invalid_signatures.join(', ')}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* AI Suggestions */}
                              {selectedDocumentAI.suggestions?.length > 0 && (
                                <div className="mb-3">
                                  <h6 className="small text-muted mb-2">
                                    <FaExclamationTriangle className="me-1" />
                                    AI Recommendations
                                  </h6>
                                  {selectedDocumentAI.suggestions.map((suggestion: any, idx: number) => (
                                    <Alert key={idx} variant="info" className="py-2 mb-2">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <strong>{suggestion.action?.replace(/_/g, ' ').toUpperCase()}:</strong>
                                          <div className="mt-1">{suggestion.message}</div>
                                        </div>
                                        <Badge bg="info">
                                          {suggestion.priority || 'medium'}
                                        </Badge>
                                      </div>
                                    </Alert>
                                  ))}
                                </div>
                              )}

                              {/* Extracted Data - Enhanced */}
                              {selectedDocumentAI.extracted_data && (
                                <div>
                                  <h6 className="small text-muted mb-2">Extracted Information</h6>
                                  <ListGroup variant="flush">
                                    {/* Property Address */}
                                    {selectedDocumentAI.extracted_data.property_address && (
                                      <ListGroup.Item className="px-0 py-1">
                                        <small>
                                          <strong>Property:</strong> {selectedDocumentAI.extracted_data.property_address}
                                        </small>
                                      </ListGroup.Item>
                                    )}
                                    
                                    {/* Transaction Type */}
                                    {selectedDocumentAI.extracted_data.transaction_type && (
                                      <ListGroup.Item className="px-0 py-1">
                                        <small>
                                          <strong>Transaction Type:</strong> {selectedDocumentAI.extracted_data.transaction_type}
                                        </small>
                                      </ListGroup.Item>
                                    )}
                                    
                                    {/* Buyers */}
                                    {selectedDocumentAI.extracted_data.buyer_names?.length > 0 && (
                                      <ListGroup.Item className="px-0 py-1">
                                        <small>
                                          <strong>Buyers:</strong> {selectedDocumentAI.extracted_data.buyer_names.join(', ')}
                                        </small>
                                      </ListGroup.Item>
                                    )}
                                    
                                    {/* Sellers */}
                                    {selectedDocumentAI.extracted_data.seller_names?.length > 0 && (
                                      <ListGroup.Item className="px-0 py-1">
                                        <small>
                                          <strong>Sellers:</strong> {selectedDocumentAI.extracted_data.seller_names.join(', ')}
                                        </small>
                                      </ListGroup.Item>
                                    )}
                                    
                                    {/* Signatures Found */}
                                    {selectedDocumentAI.extracted_data.signatures_found?.length > 0 && (
                                      <ListGroup.Item className="px-0 py-1">
                                        <small>
                                          <strong>Signatures Found:</strong> {selectedDocumentAI.extracted_data.signatures_found.join(', ')}
                                        </small>
                                      </ListGroup.Item>
                                    )}
                                    
                                    {/* Legacy parties display for backward compatibility */}
                                    {selectedDocumentAI.extracted_data.parties?.map((party: any, idx: number) => (
                                      <ListGroup.Item key={idx} className="px-0 py-1">
                                        <small>
                                          <strong>{party.role}:</strong> {party.name}
                                        </small>
                                      </ListGroup.Item>
                                    ))}
                                  </ListGroup>
                                </div>
                              )}

                              {/* Analysis Metadata */}
                              <div className="mt-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <small className="text-muted d-block">
                                      Analysis Method: {selectedDocumentAI.processing_method || 'bedrock_claude_vision'}
                                    </small>
                                    <small className="text-muted">
                                      Model: {selectedDocumentAI.ai_model || 'claude-3-sonnet'}
                                    </small>
                                  </div>
                                  <div className="text-end">
                                    <small className="text-muted d-block">
                                      Confidence: {selectedDocumentAI.confidence_score || 'N/A'}%
                                    </small>
                                    <small className="text-muted">
                                      Processing: {selectedDocumentAI.processing_time_ms || 'N/A'}ms
                                    </small>
                                  </div>
                                </div>
                                
                                {/* Compliance Status */}
                                <div className="mt-2 text-center">
                                  <Badge bg={selectedDocumentAI.validation_summary?.is_compliant ? 'success' : 'danger'} className="px-3">
                                    {selectedDocumentAI.validation_summary?.is_compliant ? '✅ COMPLIANT' : '❌ NON-COMPLIANT'}
                                  </Badge>
                                  {selectedDocumentAI.auto_approval_eligible && (
                                    <Badge bg="info" className="ms-2">
                                      Auto-Approval Eligible
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </>
                      ) : null}
                      
                      <hr className="my-3" />
                      
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <FaStickyNote className="me-1" />
                          Review Notes
                          {(reviewStatus === 'needs_revision' || reviewStatus === 'rejected') && (
                            <Badge bg="danger" className="ms-2">Required</Badge>
                          )}
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          placeholder={
                            reviewStatus === 'needs_revision' 
                              ? "Please specify what needs to be revised..."
                              : reviewStatus === 'rejected'
                              ? "Please provide rejection reason..."
                              : "Optional notes about this document..."
                          }
                          disabled={submitting}
                        />
                      </Form.Group>

                      {/* Stamp Options - Show only when approving */}
                      {reviewStatus === 'approved' && (
                        <Card className="mb-3 border-success">
                          <Card.Header className="bg-success text-white d-flex align-items-center">
                            <FaStamp className="me-2" />
                            Broker Approval Stamp {!selectedDocument?.has_stamped_version && '(Applied Automatically)'}
                          </Card.Header>
                          
                          {selectedDocument?.has_stamped_version && (
                            <Card.Body>
                              <div className="text-center text-success">
                                <FaCheckCircle className="me-2" />
                                This document has already been stamped by a broker.
                                <br />
                                <small className="text-muted">
                                  You are viewing the stamped version.
                                </small>
                              </div>
                            </Card.Body>
                          )}
                          
                          {applyStamp && !selectedDocument?.has_stamped_version && (
                            <Card.Body>
                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>
                                      <FaCalendarAlt className="me-1" />
                                      Stamp Date
                                    </Form.Label>
                                    <div>
                                      <DatePicker
                                        selected={stampDate}
                                        onChange={(date: Date) => setStampDate(date)}
                                        maxDate={new Date()}
                                        dateFormat="MM/dd/yyyy"
                                        className="form-control"
                                        placeholderText="Select stamp date"
                                      />
                                    </div>
                                    <Form.Text className="text-muted">
                                      Date that will appear on the stamp (can be backdated)
                                    </Form.Text>
                                  </Form.Group>
                                </Col>
                                
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>File Number (Optional)</Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={fileNumber}
                                      onChange={(e) => setFileNumber(e.target.value)}
                                      placeholder="Auto-generated if empty"
                                    />
                                    <Form.Text className="text-muted">
                                      Leave empty to auto-generate
                                    </Form.Text>
                                  </Form.Group>
                                </Col>
                              </Row>

                              {/* Stamp placement UI - commented out for v1 release, automatic placement in bottom right */}
                              {/*
                              <Alert variant="info" className="mb-3">
                                <div className="d-flex align-items-center mb-2">
                                  <FaCrosshairs className="me-2" />
                                  <strong>Drag elements to the document OR click element then click document:</strong>
                                </div>
                                <div className="d-flex justify-content-center flex-wrap gap-2 mb-3">
                                  <div
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('elementType', 'initials');
                                      setCurrentStampElement('initials');
                                    }}
                                    className={`btn ${currentStampElement === 'initials' ? 'btn-primary' : 'btn-outline-primary'} btn-sm d-flex align-items-center`}
                                    style={{ cursor: 'grab' }}
                                    onClick={() => setCurrentStampElement('initials')}
                                  >
                                    <FaUser className="me-1" />
                                    🖱️ Initials {stampPositions.initials && '✓'}
                                  </div>
                                  
                                  <div
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('elementType', 'date');
                                      setCurrentStampElement('date');
                                    }}
                                    className={`btn ${currentStampElement === 'date' ? 'btn-success' : 'btn-outline-success'} btn-sm d-flex align-items-center`}
                                    style={{ cursor: 'grab' }}
                                    onClick={() => setCurrentStampElement('date')}
                                  >
                                    <FaCalendarAlt className="me-1" />
                                    🖱️ Date {stampPositions.date && '✓'}
                                  </div>
                                  
                                  <div
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('elementType', 'fileNumber');
                                      setCurrentStampElement('fileNumber');
                                    }}
                                    className={`btn ${currentStampElement === 'fileNumber' ? 'btn-warning' : 'btn-outline-warning'} btn-sm d-flex align-items-center`}
                                    style={{ cursor: 'grab' }}
                                    onClick={() => setCurrentStampElement('fileNumber')}
                                  >
                                    <FaHashtag className="me-1" />
                                    🖱️ File # {stampPositions.fileNumber && '✓'}
                                  </div>
                                </div>
                                {currentStampElement && (
                                  <div className="mt-2">
                                    <small className="text-muted">
                                      Now click anywhere on the document to place the <strong>{currentStampElement}</strong>, or drag the button to the document
                                    </small>
                                  </div>
                                )}
                              </Alert>
                              */}
                              
                              <Alert variant="success" className="mb-3">
                                <FaCheckCircle className="me-2" />
                                <strong>Automatic Stamp Placement:</strong> When you approve, stamps will be automatically placed in the bottom-right corner (File #, Initials, Date)
                              </Alert>

                              {(stampPositions.initials || stampPositions.date || stampPositions.fileNumber) && (
                                <Card className="border-success mb-3">
                                  <Card.Header className="bg-light">
                                    <small><strong>Placed Elements:</strong></small>
                                  </Card.Header>
                                  <Card.Body className="py-2">
                                    <small className="text-muted d-block mb-2">
                                      💡 Click any badge to reposition or × to remove
                                    </small>
                                    <div className="d-flex flex-wrap gap-2">
                                      {stampPositions.initials && (
                                        <Badge 
                                          bg="primary" 
                                          className="d-flex align-items-center"
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => setCurrentStampElement('initials')}
                                          title="Click to reposition"
                                        >
                                          <FaUser className="me-1" /> Initials
                                          <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="text-white p-0 ms-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setStampPositions(prev => ({ ...prev, initials: null }));
                                            }}
                                            style={{ fontSize: '10px' }}
                                            title="Remove"
                                          >
                                            ×
                                          </Button>
                                        </Badge>
                                      )}
                                      {stampPositions.date && (
                                        <Badge 
                                          bg="success" 
                                          className="d-flex align-items-center"
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => setCurrentStampElement('date')}
                                          title="Click to reposition"
                                        >
                                          <FaCalendarAlt className="me-1" /> Date
                                          <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="text-white p-0 ms-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setStampPositions(prev => ({ ...prev, date: null }));
                                            }}
                                            style={{ fontSize: '10px' }}
                                            title="Remove"
                                          >
                                            ×
                                          </Button>
                                        </Badge>
                                      )}
                                      {stampPositions.fileNumber && (
                                        <Badge 
                                          bg="warning" 
                                          text="dark" 
                                          className="d-flex align-items-center"
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => setCurrentStampElement('fileNumber')}
                                          title="Click to reposition"
                                        >
                                          <FaHashtag className="me-1" /> File Number
                                          <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="text-dark p-0 ms-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setStampPositions(prev => ({ ...prev, fileNumber: null }));
                                            }}
                                            style={{ fontSize: '10px' }}
                                            title="Remove"
                                          >
                                            ×
                                          </Button>
                                        </Badge>
                                      )}
                                    </div>
                                  </Card.Body>
                                </Card>
                              )}

                              <Alert variant="info" className="mb-0">
                                <small>
                                  <strong>Preview:</strong> The stamp will appear as a bordered box with:
                                  <br />• BROKER APPROVED
                                  <br />• File #: {fileNumber || 'Auto-generated'}
                                  <br />• By: [Your Initials]
                                  <br />• Date: {stampDate.toLocaleDateString()}
                                </small>
                              </Alert>
                            </Card.Body>
                          )}
                        </Card>
                      )}
                        
                        {/* Signature Upload/Draw Section */}
                        {reviewStatus === 'approved' && applyStamp && (
                          <Card className="mt-3">
                            <Card.Header className="bg-info text-white">
                              <FaPencilAlt className="me-2" />
                              Broker Signature
                            </Card.Header>
                            <Card.Body>
                              <div className="text-center">
                                {!signatureDataUrl ? (
                                  <div>
                                    <p className="mb-3">Upload or draw your signature</p>
                                    <div className="d-flex gap-2 justify-content-center mb-3">
                                      <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => {
                                          const input = document.createElement('input');
                                          input.type = 'file';
                                          input.accept = 'image/*';
                                          input.onchange = (e) => {
                                            const file = (e.target as HTMLInputElement).files?.[0];
                                            if (file) {
                                              const reader = new FileReader();
                                              reader.onload = (event) => {
                                                setSignatureDataUrl(event.target?.result as string);
                                              };
                                              reader.readAsDataURL(file);
                                            }
                                          };
                                          input.click();
                                        }}
                                      >
                                        <FaUpload className="me-1" /> Upload Signature
                                      </Button>
                                      <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => {
                                          // TODO: Open signature pad modal
                                          toast.info('Signature pad coming soon!');
                                        }}
                                      >
                                        <FaPencilAlt className="me-1" /> Draw Signature
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div 
                                      className="border rounded p-3 mb-3 position-relative"
                                      style={{ backgroundColor: '#f8f9fa', cursor: 'grab' }}
                                      draggable
                                      onDragStart={(e) => {
                                        setIsDraggingSignature(true);
                                        e.dataTransfer.effectAllowed = 'copy';
                                        e.dataTransfer.setData('elementType', 'signature');
                                      }}
                                      onDragEnd={() => setIsDraggingSignature(false)}
                                    >
                                      <img 
                                        src={signatureDataUrl} 
                                        alt="Signature" 
                                        style={{ 
                                          maxWidth: '200px', 
                                          maxHeight: '80px',
                                          opacity: isDraggingSignature ? 0.5 : 1
                                        }} 
                                      />
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="position-absolute top-0 end-0 text-danger"
                                        onClick={() => {
                                          setSignatureDataUrl(null);
                                          setSignaturePosition(null);
                                        }}
                                      >
                                        <FaTimesCircle />
                                      </Button>
                                    </div>
                                    <p className="text-muted small mb-0">
                                      <FaInfoCircle className="me-1" />
                                      Drag your signature onto the document where you want to place it
                                    </p>
                                    {signaturePosition && (
                                      <p className="text-success small mt-2 mb-0">
                                        ✅ Signature placed at position ({signaturePosition.x.toFixed(1)}%, {signaturePosition.y.toFixed(1)}%)
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        )}

                      <Form.Group className="mb-3">
                        <Form.Label>Manual Compliance Score Override</Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Range
                            value={complianceScore}
                            onChange={(e) => setComplianceScore(Number(e.target.value))}
                            min={0}
                            max={100}
                            className="me-3"
                            disabled={submitting}
                          />
                          <Badge bg={getComplianceScoreColor(complianceScore)}>
                            {complianceScore}%
                          </Badge>
                        </div>
                        <Form.Text className="text-muted">
                          This will override the AI-generated compliance score
                        </Form.Text>
                      </Form.Group>
                    </Form>
                  </Tab>
                  
                  <Tab eventKey="history" title="History">
                    {selectedDocument.review_history?.length > 0 ? (
                      <ListGroup variant="flush">
                        {selectedDocument.review_history.map((review) => (
                          <ListGroup.Item key={review.id}>
                            <div className="d-flex justify-content-between">
                              <strong>{review.reviewed_by}</strong>
                              {getStatusBadge(review.review_status)}
                            </div>
                            <small className="text-muted">
                              {new Date(review.reviewed_at).toLocaleString()}
                            </small>
                            {review.review_notes && (
                              <p className="mb-1 mt-2">{review.review_notes}</p>
                            )}
                            {review.compliance_score && (
                              <Badge bg={getComplianceScoreColor(review.compliance_score)}>
                                Score: {review.compliance_score}%
                              </Badge>
                            )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <Alert variant="info">
                        No review history for this document.
                      </Alert>
                    )}
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReviewModal(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              console.log('Submit button onClick fired!');
              submitReview();
            }}
            disabled={
              !reviewStatus || 
              submitting || 
              ((reviewStatus === 'needs_revision' || reviewStatus === 'rejected') && !reviewNotes)
            }
            title={
              !reviewStatus ? 'Please select a review action' :
              ((reviewStatus === 'needs_revision' || reviewStatus === 'rejected') && !reviewNotes) ? 'Please add review notes' :
              submitting ? 'Submitting...' :
              'Click to submit review'
            }
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {reviewStatus === 'approved' && applyStamp ? 'Approving & Stamping...' : 'Submitting...'}
              </>
            ) : (
              reviewStatus === 'approved' && applyStamp ? (
                <>
                  <FaStamp className="me-1" />
                  Approve & Apply Stamp
                </>
              ) : (
                `Submit Review ${!reviewStatus ? '(Select Action)' : ''}`
              )
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DocumentReviewInterface;